import { createContext, useState, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { RolRouter } from '../../router/RolRouter';
import { SCOPES_BY_ROLES } from '../../utils/ROLES';
import { AppHeader } from './AppHeader';
import { NavBar2 } from './NavBar2';
import { useAppSelector } from '../../store/store';
import { LitrosPorMeses } from '../../pages/LitrosPorMeses';
import { Home } from '../../pages/Home';
import { Bancos } from '../../pages/Bancos';
import { Reportes } from '../../pages/Reportes';
import { Proveedores } from '../../pages/Proveedores';
import { Rutas } from '../../pages/Rutas';
import { Conductores } from '../../pages/Conductores';
import { Cupos } from '../../pages/Cupos';
import { Pesos } from '../../pages/Pesos';
import { PagoConductores } from '../../pages/PagoConductores';
import { PATHS } from '../../utils/Constant';
import { Tinas } from "../../pages/Tinas";
import { ElementoFabrica } from '../../pages/ElementoFabrica';
import { RegistroFabrica } from '../../pages/RegistroFabrica';
import { RecetaFabrica } from '../../pages/RecetaFabrica';
import { ReportesS3 } from '../../pages/ReportesS3';

import { GraficasMovimiento } from '../../pages/GraficasMovimiento'; // Evidelis lo colocó 
import { AlertasMonitoreo } from "../../pages/AlertasMonitoreo"; // Evidelis lo colocó 
import { LicenciaModal } from '../licencia/LicenciaModal';
import { getLicenciaEstado, LicenciaEstado } from '../../services/licenciaService';




interface TitleContexProps {
    titlesPage: TitlesPageProps;
    setTitlesPage: React.Dispatch<React.SetStateAction<TitlesPageProps>>
}
export interface TitlesPageProps {
    title: string;
    subTitle: string;
}

export const TitleContex = createContext({} as TitleContexProps);

const LICENCIA_REFRESH_MS = 60 * 60 * 1000;
const DEFAULT_FRECUENCIA_MODAL_MINUTOS = 1440;

const getLicenciaDismissKey = (licencia: LicenciaEstado | null) =>
    `licencia-renovacion-${licencia?.fecha_vencimiento || 'sin-fecha'}`;

const getFrecuenciaModalMs = (licencia: LicenciaEstado | null) => {
    const frecuenciaMinutos = Number(licencia?.frecuencia_modal_minutos || DEFAULT_FRECUENCIA_MODAL_MINUTOS);
    return Math.max(frecuenciaMinutos, 1) * 60 * 1000;
};

const getUltimoCierreLicencia = (licencia: LicenciaEstado | null) => {
    try {
        return Number(window.localStorage.getItem(getLicenciaDismissKey(licencia)) || 0);
    } catch {
        return 0;
    }
};

const isLicenciaModalEnEspera = (licencia: LicenciaEstado | null) =>
    Date.now() - getUltimoCierreLicencia(licencia) < getFrecuenciaModalMs(licencia);

const shouldShowLicenciaModal = (licencia: LicenciaEstado) => {
    if (licencia?.debe_bloquear) return true;
    return Boolean(licencia?.mostrar_modal_pago_pendiente && !isLicenciaModalEnEspera(licencia));
};

const dismissLicenciaModal = (licencia: LicenciaEstado | null) => {
    try {
        window.localStorage.setItem(getLicenciaDismissKey(licencia), String(Date.now()));
    } catch { }
};

export const Dashboard = () => {
    const rol = useAppSelector(state => state.auth.rol);
    const [titlesPage, setTitlesPage] = useState({} as TitlesPageProps)
    const [showNav, setShowNav] = useState(false);
    const [screenIsSmall, setScreenIsSmall] = useState(document.body.clientWidth < 768);
    const [licencia, setLicencia] = useState<LicenciaEstado | null>(null);
    const [showLicenciaModal, setShowLicenciaModal] = useState(false);
    const [licenciaConsultada, setLicenciaConsultada] = useState(false);

    useEffect(() => {
        const onResize = () => setScreenIsSmall(document.body.clientWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        let isMounted = true;
        const consultarLicencia = async () => {
            try {
                const estadoLicencia = await getLicenciaEstado();
                if (!isMounted) return;
                setLicencia(estadoLicencia);
                setShowLicenciaModal(shouldShowLicenciaModal(estadoLicencia));
            } catch (error) {
                console.log(error);
            } finally {
                if (isMounted) setLicenciaConsultada(true);
            }
        };
        consultarLicencia();
        const intervalId = window.setInterval(consultarLicencia, LICENCIA_REFRESH_MS);
        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
        };
    }, []);

    const licenciaBloqueada = Boolean(licencia?.debe_bloquear);
    const puedeUsarSistema = licenciaConsultada && !licenciaBloqueada;

    useEffect(() => {
        if (!licencia || licenciaBloqueada || showLicenciaModal || !licencia.mostrar_modal_pago_pendiente) return;
        const tiempoRestante = getFrecuenciaModalMs(licencia) - (Date.now() - getUltimoCierreLicencia(licencia));
        if (tiempoRestante <= 0) {
            setShowLicenciaModal(true);
            return;
        }
        const timeoutId = window.setTimeout(() => setShowLicenciaModal(true), tiempoRestante);
        return () => window.clearTimeout(timeoutId);
    }, [licencia, licenciaBloqueada, showLicenciaModal]);

    const onCloseLicenciaModal = () => {
        if (licenciaBloqueada) return;
        dismissLicenciaModal(licencia);
        setShowLicenciaModal(false);
    };

    return (
        <div>
            <AppHeader setShowNav={setShowNav} showNav={showNav} screenIsSmall={screenIsSmall} />
            {puedeUsarSistema && <NavBar2 setShowNav={setShowNav} showNav={showNav} screenIsSmall={screenIsSmall} />}
            <div id='main' style={{ zIndex: 0, maxWidth: '100vw' }} className="mx-5 overflow-hidden">
                {puedeUsarSistema && (
                    <TitleContex.Provider value={{ titlesPage, setTitlesPage }}>
                        <Routes>
                            <Route index element={<Home />} />
                            <Route path="/rutas" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={Rutas} />}
                            />
                            <Route path="/conductores" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={Conductores} />}
                            />
                            <Route path="/catalogo-banco" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={Bancos} />}
                            />
                            <Route path="/cupos" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={Cupos} />}
                            />
                            <Route path="/proveedores" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={Proveedores} />}
                            />
                            <Route path="/pesos" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_1} userRoles={rol} component={Pesos} />}
                            />
                            <Route path={`/${PATHS.REPORTES.CONSOLIDADO_PROVEEDOR}`} element={
                                <RolRouter key={PATHS.REPORTES.CONSOLIDADO_PROVEEDOR} needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={Reportes} />}
                            />
                            <Route path={`/${PATHS.REPORTES.ESTRACTO_D_PROVEEDOR}`} element={
                                <RolRouter key={PATHS.REPORTES.ESTRACTO_D_PROVEEDOR} needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={Reportes} />}
                            />
                            <Route path={`/${PATHS.REPORTES.ESTRACTO_M_PROVEEDOR}`} element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={LitrosPorMeses} />}
                            />
                            <Route path={`/${PATHS.REPORTES.CONSOLIDADO_CONDUCTOR}`} element={
                                <RolRouter key={PATHS.REPORTES.CONSOLIDADO_CONDUCTOR} needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={PagoConductores} />}
                            />
                            <Route path={`/${PATHS.REPORTES.ESTRACTO_D_CONDUCTOR}`} element={
                                <RolRouter key={PATHS.REPORTES.ESTRACTO_D_CONDUCTOR} needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={PagoConductores} />}
                            />
                            <Route path="/tinas" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={Tinas} />}
                            />
                            <Route path="/elemento-fabrica" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={ElementoFabrica} />}
                            />
                            <Route path="/receta-fabrication" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={RecetaFabrica} />}

                            />
                            <Route path="/registro-fabrica" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={RegistroFabrica} />}
                            />
                            <Route path="/reportes-s3" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_0} userRoles={rol} component={ReportesS3} />}
                            />

                            { /*Evidelis lo colocó*/}
                            <Route path="/graficas-movimiento" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={GraficasMovimiento} />
                            } />



                            { /*ALERTAS - Evidelis lo colocó*/}
                            <Route path="/alertas-monitoreo" element={
                                <RolRouter needScope={SCOPES_BY_ROLES.SCP_2} userRoles={rol} component={AlertasMonitoreo} />}
                            />

                        </Routes>
                    </TitleContex.Provider>
                )}
            </div>
            <LicenciaModal
                licencia={licencia}
                show={showLicenciaModal || licenciaBloqueada}
                bloqueada={licenciaBloqueada}
                onClose={onCloseLicenciaModal}
            />
        </div>
    )
}
